import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import Select from 'react-select';
import {
  createFeatureToggle,
  createOrUpdateFeatureToggle,
  FeatureToggle,
  FeatureToggleCondition
} from '../../../api/featureManagement/featureToggleService';
import { listFeatures, Feature } from '../../../api/featureManagement/featureService';
import {
  COMPARATOR_OPTIONS,
  buildConditionValue,
  displayConditionValue,
} from '../conditionUtils';

interface FeatureToggleFormModalProps {
  show: boolean;
  handleClose: () => void;
  onSuccess: () => void;
  toggle?: FeatureToggle | null;
  clientId?: string;
}

const STATUS_OPTIONS = [
  { value: 'ENABLED', label: 'Enabled' },
  { value: 'DISABLED', label: 'Disabled' },
];

const FeatureToggleFormModal: React.FC<FeatureToggleFormModalProps> = ({
  show,
  handleClose,
  onSuccess,
  toggle,
  clientId: propClientId
}) => {
  const [formData, setFormData] = useState({
    featureId: '',
    scope: '',
    clientId: '',
    entityType: '',
    entityValue: '',
    status: 'ENABLED' as 'ENABLED' | 'DISABLED',
    conditions: [] as FeatureToggleCondition[]
  });

  const [features, setFeatures] = useState<Feature[]>([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      loadFeatures();
    }
  }, [show]);

  const loadFeatures = async () => {
    try {
      setLoadingFeatures(true);
      const response = await listFeatures({ page: 1, pageSize: 100 });
      setFeatures(response.paginatedResults || []);
    } catch (err) {
      console.error('Error loading features:', err);
    } finally {
      setLoadingFeatures(false);
    }
  };

  useEffect(() => {
    if (!show) return;

    if (toggle) {
      const t = toggle as any;
      const featureName = t.feature?.name ?? t.featureName ?? t.featureid ?? t.featureId ?? '';
      const matchedFeature = features.find((f) => f.name === featureName);

      const statusFromEnabled =
        typeof t.enabled === 'boolean'
          ? t.enabled
            ? 'ENABLED'
            : 'DISABLED'
          : t.status === 'KILLED'
            ? 'DISABLED'
            : t.status;

      const conditionsList: FeatureToggleCondition[] = Array.isArray(t.conditions)
        ? t.conditions.map((c: any) => ({
            name: c.name ?? '',
            comparator: String(c.comparator ?? 'EQ').toUpperCase(),
            value: displayConditionValue(c.value),
          }))
        : [];

      setFormData({
        featureId: featureName,
        scope: t.scope ?? t.feature?.scope ?? t.featurescope ?? '',
        clientId: matchedFeature?.clientId ?? t.clientId ?? t.clientid ?? '',
        entityType: t.entityType ?? t.entitytype ?? '',
        entityValue: t.entityValue ?? t.entityvalue ?? '',
        status: (statusFromEnabled === 'ENABLED' || statusFromEnabled === 'DISABLED' ? statusFromEnabled : 'ENABLED') as 'ENABLED' | 'DISABLED',
        conditions: conditionsList,
      });
    } else {
      setFormData({
        featureId: '',
        scope: '',
        clientId: '',
        entityType: '',
        entityValue: '',
        status: 'ENABLED',
        conditions: []
      });
    }

    setErrors({});
    setSubmitError(null);
  }, [show, toggle, features]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!(formData.featureId ?? '').trim()) {
      newErrors.featureId = 'Feature is required';
    }

    if (!formData.scope) {
      newErrors.scope = 'Scope is required';
    }

    if (!formData.clientId) {
      newErrors.clientId = 'Client ID is required';
    }

    if (!formData.entityType) {
      newErrors.entityType = 'Entity Type is required';
    }

    if (!(formData.entityValue ?? '').trim()) {
      newErrors.entityValue = 'Entity Value is required';
    }

    if (!formData.status) {
      newErrors.status = 'Status is required';
    }

    formData.conditions.forEach((cond, index) => {
      if (!cond.name.trim()) {
        newErrors[`condition_${index}_name`] = 'Condition name is required';
      }
      if (!cond.comparator) {
        newErrors[`condition_${index}_comparator`] = 'Comparator is required';
      }
      if (cond.value === undefined || cond.value === null || String(cond.value).trim() === '') {
        newErrors[`condition_${index}_value`] = 'Value is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const hasConditions = formData.conditions.length > 0;

      const payload = {
        featureName: (formData.featureId ?? '').trim(),
        featureScope: formData.scope,
        clientId: formData.clientId,
        entityType: formData.entityType,
        entityValue: (formData.entityValue ?? '').trim(),
        enabled: formData.status === 'ENABLED',
        conditions: hasConditions
          ? formData.conditions.map((condition) => ({
              name: condition.name,
              comparator: condition.comparator,
              value: buildConditionValue(condition.comparator, String(condition.value)),
            }))
          : undefined,
        setConditions: hasConditions,
      };

      if (toggle) {
        await createOrUpdateFeatureToggle(payload as any);
      } else {
        await createFeatureToggle(payload as any);
      }

      onSuccess();
      handleClose();
    } catch (err: any) {
      console.error('Error saving feature toggle:', err);
      setSubmitError(err?.response?.data?.message || 'Failed to save feature toggle. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddCondition = () => {
    setFormData(prev => ({
      ...prev,
      conditions: [...prev.conditions, { name: '', comparator: 'EQ', value: '' }]
    }));
  };

  const handleRemoveCondition = (index: number) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const handleConditionChange = (index: number, field: keyof FeatureToggleCondition, value: any) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map((cond, i) =>
        i === index ? { ...cond, [field]: value } : cond
      )
    }));
    if (errors[`condition_${index}_${field}`]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[`condition_${index}_${field}`];
        return next;
      });
    }
  };

  const featureOptions = features.map(f => ({
    value: f.name,
    label: f.name
  }));

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{toggle ? 'Edit Feature Toggle' : 'Create New Feature Toggles'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {submitError && <Alert variant="danger">{submitError}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label>
              Select Feature<span style={{ color: 'red' }}>*</span>
            </Form.Label>
            <Select
              classNamePrefix="react-select"
              value={featureOptions.find(opt => opt.value === formData.featureId)}
              onChange={(option) => {
                const selectedName = option?.value || '';
                const selectedFeature = features.find(f => f.name === selectedName);
                setFormData(prev => ({
                  ...prev,
                  featureId: selectedName,
                  clientId: selectedFeature?.clientId ?? prev.clientId,
                }));
                if (errors.featureId) setErrors(prev => ({ ...prev, featureId: '' }));
                if (errors.clientId) setErrors(prev => ({ ...prev, clientId: '' }));
              }}
              options={featureOptions}
              isClearable={false}
              isLoading={loadingFeatures}
              isDisabled={!!toggle}
              placeholder="Select a feature"
            />
            {errors.featureId && <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>{errors.featureId}</div>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Scope<span style={{ color: 'red' }}>*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.scope}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, scope: e.target.value }));
                if (errors.scope) setErrors(prev => ({ ...prev, scope: '' }));
              }}
              isInvalid={!!errors.scope}
              placeholder="e.g., tournament"
            />
            {errors.scope && <Form.Control.Feedback type="invalid">{errors.scope}</Form.Control.Feedback>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Client ID<span style={{ color: 'red' }}>*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.clientId}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, clientId: e.target.value }));
                if (errors.clientId) setErrors(prev => ({ ...prev, clientId: '' }));
              }}
              isInvalid={!!errors.clientId}
              placeholder="e.g., client_id"
              disabled={!!toggle}
              style={toggle ? { pointerEvents: 'none' } : undefined}
            />
            {errors.clientId && <Form.Control.Feedback type="invalid">{errors.clientId}</Form.Control.Feedback>}
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Entity Type<span style={{ color: 'red' }}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.entityType}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, entityType: e.target.value }));
                    if (errors.entityType) setErrors(prev => ({ ...prev, entityType: '' }));
                  }}
                  isInvalid={!!errors.entityType}
                  placeholder="e.g., user_id"
                />
                {errors.entityType && <Form.Control.Feedback type="invalid">{errors.entityType}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Entity Value<span style={{ color: 'red' }}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.entityValue}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, entityValue: e.target.value }));
                    if (errors.entityValue) setErrors(prev => ({ ...prev, entityValue: '' }));
                  }}
                  isInvalid={!!errors.entityValue}
                  placeholder="e.g., 12345"
                />
                {errors.entityValue && <Form.Control.Feedback type="invalid">{errors.entityValue}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>
              Status<span style={{ color: 'red' }}>*</span>
            </Form.Label>
            <Select
              classNamePrefix="react-select"
              value={STATUS_OPTIONS.find(opt => opt.value === formData.status)}
              onChange={(option) => {
                setFormData(prev => ({ ...prev, status: option?.value as any || 'ENABLED' }));
                if (errors.status) setErrors(prev => ({ ...prev, status: '' }));
              }}
              options={STATUS_OPTIONS}
              isClearable={false}
            />
            {errors.status && <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>{errors.status}</div>}
          </Form.Group>

          {/* Conditions Section */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="mb-0 fw-bold">Conditions</Form.Label>
              <Button
                variant="outline-primary"
                size="sm"
                type="button"
                onClick={handleAddCondition}
              >
                + Add Condition
              </Button>
            </div>

            {formData.conditions.length === 0 ? (
              <div className="text-muted" style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                No conditions added. Click "Add Condition" to add one.
              </div>
            ) : (
              formData.conditions.map((condition, index) => (
                <div key={index} className="mb-3">
                  <Row>
                    <Col md={5}>
                      <Form.Group className="mb-2">
                        <Form.Label style={{ fontSize: '0.875rem' }}>Condition Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={condition.name}
                          onChange={(e) => handleConditionChange(index, 'name', e.target.value)}
                          isInvalid={!!errors[`condition_${index}_name`]}
                          placeholder="e.g., purchase_count"
                        />
                        {errors[`condition_${index}_name`] && (
                          <Form.Control.Feedback type="invalid">{errors[`condition_${index}_name`]}</Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label style={{ fontSize: '0.875rem' }}>Comparator</Form.Label>
                        <Select
                          classNamePrefix="react-select"
                          value={COMPARATOR_OPTIONS.find(opt => opt.value === condition.comparator) || null}
                          onChange={(option) => handleConditionChange(index, 'comparator', option?.value || 'EQ')}
                          options={COMPARATOR_OPTIONS}
                          isClearable={false}
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderColor: errors[`condition_${index}_comparator`] ? '#dc3545' : base.borderColor,
                            }),
                          }}
                        />
                        {errors[`condition_${index}_comparator`] && (
                          <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                            {errors[`condition_${index}_comparator`]}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-2">
                        <Form.Label style={{ fontSize: '0.875rem' }}>Value</Form.Label>
                        <Form.Control
                          type="text"
                          value={condition.value}
                          onChange={(e) => handleConditionChange(index, 'value', e.target.value)}
                          isInvalid={!!errors[`condition_${index}_value`]}
                          placeholder={
                            ['IN', 'NOTIN'].includes(condition.comparator)
                              ? 'e.g., gold, platinum'
                              : 'e.g., 5'
                          }
                        />
                        {errors[`condition_${index}_value`] && (
                          <Form.Control.Feedback type="invalid">{errors[`condition_${index}_value`]}</Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                  <div className="text-end">
                    <Button
                      variant="link"
                      size="sm"
                      type="button"
                      onClick={() => handleRemoveCondition(index)}
                      className="text-danger p-0"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : toggle ? 'Update Feature Toggle' : 'Create Feature Toggle'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default FeatureToggleFormModal;
